import { Box, Button, CircularProgress, IconButton, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { Circle, CircleAlert, Clock } from 'lucide-react';
import React, { FC, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import Divider from 'components/divider';
import FlexBox from 'components/flex-box';
import LoadingSpinner from 'components/loading-spinner';

import { AxiosError, AxiosResponse } from 'axios';
import Drawer, { DrawerHandle } from 'components/drawer';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePost } from 'hooks/use-post';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { ErrorMessage } from 'types/shared/error-message.type';
import { skipManualRecommendation } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/api/update-ofd-settings';
import CreateOrderForm, {
  DefaultFormValues,
} from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/orders/form/create-order-form';
import { useInputSettingsState } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/input-settings-context';
import { useOrdersState } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/orders-context';
import { useCreateOrder } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/hooks/use-create-order';
import { CreateOrderFormValues } from 'views/order-feed/features/my-supply-plan/types/create-order-form-values.type';
import { OFD2Params } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-2-params.type';
import { Recommendation } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/recommendation-assortment.type';
import { SkipManualRecommendation } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/skip-manual-recommendation.type';
import { useGoogleTagManager } from 'views/order-feed/features/my-supply-plan/hooks/use-google-tag-manager';
import { OrderType, SupplyPlanType } from 'views/order-feed/features/my-supply-plan/types/google-tag-manager/order-feed-events.type';

type Props = {
  recommendation: Recommendation;
};

type ManualSkipBoxProps = {
  handleCancelAction: () => void;
  dispatchDate: string;
};

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.orders';

const OverrideSubmitButton: FC<{ isPending: boolean }> = ({ isPending }) => {
  const {
    formState: { isValid },
  } = useFormContext<CreateOrderFormValues>();

  return (
    <Button
      startIcon={isPending ? <CircularProgress color="inherit" size={18} /> : null}
      type="submit"
      disabled={isPending || !isValid}>
      <FormattedMessage id={`${PAGE_NAME_ID}.modals.forecastDialog.overrideButton`} />
    </Button>
  );
};

const ModalTitle = () => {
  const intl = useIntl();
  const theme = useTheme();

  return (
    <Box display={'flex'} justifyContent={'center'} alignItems={'center'} gap={3}>
      <Box>
        <Typography fontWeight={'bold'}>
          {intl.formatMessage({
            id: `${PAGE_NAME_ID}.modals.forecastDialog.title`,
            defaultMessage: 'orderStatus', // what was this supposed to be?
          })}
        </Typography>
      </Box>
      <IconButton sx={{ backgroundColor: theme.palette.orange.light, cursor: 'unset' }} disableRipple disableTouchRipple size="small">
        <Clock color="black" size={17} />
      </IconButton>
    </Box>
  );
};

const ManualRecommendationSkipBox: React.FC<ManualSkipBoxProps> = ({ handleCancelAction, dispatchDate }) => {
  const intl = useIntl();
  const params = useParams<OFD2Params>();

  const { isLoading } = useOrdersState();

  const snackbarDispatch = useSnackbarDispatch();
  const queryClient = useQueryClient();

  const manualSkip = usePost<AxiosResponse, SkipManualRecommendation, AxiosError<ErrorMessage[]>>(
    (request) => skipManualRecommendation(request, params.globalSiteId),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      [['getTimeline'], ['getOfdSettings'], ['getOrderAssortment'], ['getCustomerAssortment'], ['getRecommendationAssortment']].forEach(
        (queryKey) => {
          queryClient.invalidateQueries({ queryKey });
        }
      );
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error, errorMsg: error.response?.data },
      });
    }
  );

  return (
    <Box>
      <Typography fontWeight={500} textAlign={'center'}>
        <FormattedMessage
          id={`${PAGE_NAME_ID}.modals.confirmedDialog.skipRecommendation.skipRecommendationConfirmationMessage`}
          values={{ date: dayjs(dispatchDate).format('DD MMM') }}
        />
      </Typography>
      <Box display={'flex'} justifyContent={'space-between'} mt={4}>
        <Button
          startIcon={manualSkip.isPending ? <CircularProgress color="inherit" size={18} /> : null}
          variant="outlined"
          color="error"
          disabled={manualSkip.isPending}
          onClick={() => manualSkip.mutate({ skip: true, dispatchDate })}>
          <FormattedMessage id={`${PAGE_NAME_ID}.modals.confirmedDialog.skipButton`} />
        </Button>
        <Button onClick={handleCancelAction} disabled={isLoading || manualSkip.isPending}>
          <FormattedMessage id={`general.cancel`} />
        </Button>
      </Box>
    </Box>
  );
};

const OverrideForecastDialog: React.FC<Props> = ({ recommendation }) => {
  const intl = useIntl();
  const drawerRef = useRef<DrawerHandle>(null);
  const { buildOrderObject, overrideOFDOrder } = useCreateOrder();
  const params = useParams<OFD2Params>();
  const { createOrderFeedEvent } = useGoogleTagManager();
  const queryClient = useQueryClient();
  const {
    getOfdSettingsQuery: { data: ofdSettings, isLoading: isOfdSettingsLoading },
  } = useInputSettingsState();

  const { orderSettingsQuery } = useOrdersState();

  const [skippingOrder, setSkippingOrder] = useState(false);

  const defaultFormValues: DefaultFormValues = useMemo(
    () => ({
      dispatchDate: recommendation.dispatch,
      siteErpId: params.siteErpId,
      plantId: recommendation.plantId,
      vessel: recommendation.vessel,
      orderLines: recommendation.products.map((product) => ({
        orderQuantity: product.quantity,
        itemNumber: product.productNumber,
      })),
    }),
    [recommendation]
  );

  const forecastProductNames: Record<string, string> = useMemo(
    () =>
      recommendation.products.reduce<Record<string, string>>((acc, product) => {
        acc[product.productNumber] = product.name;
        return acc;
      }, {}),
    [recommendation]
  );

  const handleSubmit = (data: CreateOrderFormValues) => {
    overrideOFDOrder.mutate(
      { data: buildOrderObject(data) },
      {
        onSuccess: ({ orderNumber, createOrderInput }) => {
          const oldTotalQuantity = recommendation.products.reduce((prev, curr) => prev + curr.quantity, 0);
          const newTotalQuantity = createOrderInput.orderLines.reduce((prev, curr) => Number(prev) + Number(curr.orderQuantity), 0);

          createOrderFeedEvent('override_forecast', {
            orderType: OrderType.FORECAST,
            supplyPlanType: SupplyPlanType.OFD2,
            oldValue1: String(oldTotalQuantity),
            newValue1: String(newTotalQuantity),
            dailyConsumptionCalculationMethod: String(ofdSettings?.consumptionRateAdjustmentMethod),
            consumptionRateAdjustmentDays: String(ofdSettings?.consumptionRateAdjustmentDays),
          });

          [['getOrderAssortment'], ['getCustomerAssortment'], ['getRecommendationAssortment']].forEach((queryKey) => {
            queryClient.invalidateQueries({ queryKey });
          });
          drawerRef.current?.close();
        },
      }
    );
  };

  return (
    <Drawer
      ref={drawerRef}
      onClose={() => setSkippingOrder(false)}
      title={<ModalTitle />}
      button={
        <Box display={'flex'} justifyContent={'end'}>
          <Button size="small" variant="outlined" disabled={orderSettingsQuery.data?.isUnderMaintenance}>
            <FormattedMessage id={`${PAGE_NAME_ID}.list.overrideForecastBtn`} values={{ type: '' }} />
          </Button>
        </Box>
      }>
      <Box display={'flex'} flexDirection={'column'} width={600} gap={4}>
        <Box display={'flex'} alignItems={'center'} gap={3}>
          <Circle color="#FFAE64" size={16} fill="#FFAE64" />
          <Typography fontSize={14} color={'#012231'}>
            {intl.formatMessage({ id: `${PAGE_NAME_ID}.modals.forecastDialog.quantityMessage` })}
          </Typography>
        </Box>
        <Box>
          {false && <LoadingSpinner overlay />}
          {!skippingOrder ? (
            <FlexBox>
              <CreateOrderForm onSubmit={handleSubmit} defaultFormValues={defaultFormValues} forecastProductNames={forecastProductNames}>
                <Box borderRadius={2} p={5} sx={{ background: '#FFEAD6' }} display={'flex'} gap={4} flexDirection={'column'}>
                  <Box display={'flex'} gap={2}>
                    <Box display={'flex'} alignItems={'center'}>
                      <CircleAlert size={20} />
                    </Box>
                    <Box>
                      <Typography fontWeight={'bold'}>
                        <FormattedMessage
                          id={`${PAGE_NAME_ID}.modals.forecastDialog.overridingMessage`}
                          values={{ date: dayjs(recommendation.dispatch).format('DD MMM') }}
                        />
                      </Typography>
                      <Typography>
                        <FormattedMessage id={`${PAGE_NAME_ID}.modals.forecastDialog.existingForecastMessage`} />
                      </Typography>
                      <Typography>
                        <FormattedMessage id={`${PAGE_NAME_ID}.modals.forecastDialog.remainingForecastMessage`} />
                      </Typography>
                    </Box>
                  </Box>
                  <Divider dark flexItem />
                  <Typography fontWeight={'bold'}>
                    {intl.formatMessage(
                      { id: `${PAGE_NAME_ID}.modals.forecastDialog.calibrateMessage` },
                      {
                        calibrate: (
                          <span style={{ textDecoration: 'underline' }}>
                            {intl.formatMessage({ id: `${PAGE_NAME_ID}.modals.forecastDialog.calibrateSettings` })}
                          </span>
                        ),
                      }
                    )}
                  </Typography>
                </Box>
                <Box display={'flex'} justifyContent={'space-between'}>
                  <Button type="button" onClick={() => setSkippingOrder(true)} variant="outlined">
                    <FormattedMessage id={`${PAGE_NAME_ID}.modals.confirmedDialog.skipButton`} />
                  </Button>
                  <OverrideSubmitButton isPending={overrideOFDOrder.isPending} />
                </Box>
              </CreateOrderForm>
            </FlexBox>
          ) : (
            <ManualRecommendationSkipBox dispatchDate={recommendation.dispatch} handleCancelAction={() => setSkippingOrder(false)} />
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default OverrideForecastDialog;
